// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated
import { useMemo, useRef, useCallback, useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Map, ExternalLink, Pencil, Check } from 'lucide-react';
import { useProcessMap } from '@/api/queries/useProcessMap';
import { useAuthStore } from '@/stores/auth-store';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  VisualizationFullscreenOverlay
} from '@bliks-io/ui';
import {
  VisualizationFallback,
  VisualizationToolbar
} from '@/pages/organization/systems/components/VisualizationControls';
import type { EditorActions } from '@/pages/processes/process-map/components/ProcessMap2D';
import type { GetProcessMapResponse } from '@bliks-io/contracts';

const ProcessMap2D = lazy(() => import('@/pages/processes/process-map/components/ProcessMap2D'));

type MapData = GetProcessMapResponse['data'];

// ============================================================================
// Exported Card Component
// ============================================================================

interface ProcessConnectionsMapCardProps {
  processId: string;
  canEdit?: boolean;
}

export function ProcessConnectionsMapCard({
  processId,
  canEdit = false
}: ProcessConnectionsMapCardProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const [isNearViewport, setIsNearViewport] = useState(false);
  const {
    data: mapData,
    isLoading: isMapLoading,
    refetch
  } = useProcessMap({ enabled: isNearViewport });
  const isLoading = !isNearViewport || isMapLoading;
  const resetRef = useRef<(() => void) | null>(null);
  const editorActionsRef = useRef<EditorActions | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEditorMode, setIsEditorMode] = useState(false);

  const toggleFullscreen = useCallback(() => setIsFullscreen((v) => !v), []);
  const handleReset = useCallback(() => {
    resetRef.current?.();
  }, []);

  // The map query pulls the whole org landscape, so it waits until the card is
  // about to be seen rather than competing with the page's first paint.
  const observeMapArea = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries, obs) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNearViewport(true);
          obs.disconnect();
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Filter data to show only current process + direct neighbors (1 hop)
  const { filteredData, allEdges, hasConnections } = useMemo(() => {
    if (!mapData) return { filteredData: null, allEdges: [], hasConnections: false };

    // Find all direct neighbors via edges
    const neighborIds = new Set<string>();
    neighborIds.add(processId);

    for (const edge of mapData.edges) {
      if (edge.source === processId) neighborIds.add(edge.target);
      if (edge.target === processId) neighborIds.add(edge.source);
    }

    const hasConn = neighborIds.size > 1;

    const processes = mapData.processes.filter((p) => neighborIds.has(p._id));
    const edges = mapData.edges.filter(
      (e) => neighborIds.has(e.source) && neighborIds.has(e.target)
    );

    const filtered: MapData = {
      ...mapData,
      processes,
      edges
    };

    return { filteredData: filtered, allEdges: mapData.edges, hasConnections: hasConn };
  }, [mapData, processId]);

  const renderMap = (fullscreen: boolean) => (
    <Suspense fallback={<VisualizationFallback height={300} />}>
      <ProcessMap2D
        data={filteredData!}
        allEdges={allEdges}
        allProcesses={mapData?.processes}
        onResetRef={resetRef}
        editorActionsRef={editorActionsRef}
        isFullscreen={fullscreen}
        isEditorMode={isEditorMode}
        focusProcessId={processId}
        showMinimap={false}
      />
    </Suspense>
  );

  const showVisualization = !!filteredData;
  const showMap = showVisualization;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Map className="w-4 h-4" />
              {t('processMap.miniMap.title')}
            </CardTitle>
            <div className="flex items-center gap-2">
              {showMap && (
                <VisualizationToolbar
                  show3DControls={false}
                  isFullscreen={false}
                  isRotating={false}
                  onToggleRotation={() => {}}
                  onReset={handleReset}
                  onToggleFullscreen={toggleFullscreen}
                />
              )}
              {canEdit &&
                (isEditorMode ? (
                  <Button variant="outline" size="small" onClick={() => setIsEditorMode(false)}>
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    {t('common.Done')}
                  </Button>
                ) : (
                  <Button variant="outline" size="small" onClick={() => setIsEditorMode(true)}>
                    <Pencil className="w-3.5 h-3.5 mr-1.5" />
                    {t('processMap.editorMode')}
                  </Button>
                ))}
              <Link
                to="/$slug/processes/process-map"
                params={{ slug }}
                search={{ focusProcessId: processId }}
                target="_blank"
              >
                <Button variant="outline" size="small">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  {t('processMap.miniMap.showInLandscape')}
                </Button>
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div ref={observeMapArea} className="h-[300px] relative">
            {isLoading && <VisualizationFallback height={300} />}
            {!isLoading && !showMap && (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
                <span className="text-sm">{t('errors.unknown')}</span>
                <Button variant="outline" size="small" onClick={() => refetch()}>
                  {t('common.retry')}
                </Button>
              </div>
            )}
            {!isLoading && showMap && !isFullscreen && (
              <>
                {renderMap(false)}
                {!hasConnections && (
                  <div className="absolute bottom-2 left-0 right-0 flex justify-center pointer-events-none">
                    <span className="text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded-sm">
                      {t('processMap.miniMap.noConnections')}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {isFullscreen &&
        showVisualization &&
        createPortal(
          <VisualizationFullscreenOverlay
            icon={Map}
            title={t('processMap.miniMap.title')}
            onClose={() => setIsFullscreen(false)}
            controls={
              <VisualizationToolbar
                show3DControls={false}
                isFullscreen
                isRotating={false}
                onToggleRotation={() => {}}
                onReset={handleReset}
                onToggleFullscreen={toggleFullscreen}
              />
            }
          >
            {renderMap(true)}
          </VisualizationFullscreenOverlay>,
          document.body
        )}
    </>
  );
}
